import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { BasecampLayout, AreaLayout } from '../../components/Layout';
import { PageHeader, Card, Spinner, Alert, Badge, Btn, Input, Select, EmptyState } from '../../components/UI';
import { api } from '../../utils/api';

// Contribute (What I give) — Open Renovation:
//   Propose a Content   → forma (headline, opis, oblast, fajl) → custodian-ima
//   My proposed content → moji predlozi + status
//   My approvals        → red za custodian-a (predlozi u mojim oblastima)
// Odobreni predlozi se pojavljuju u Grow → Content by Open Renovation.

const SUBNAV = [
  { href: '/contribute/propose', label: 'Propose a Content' },
  { href: '/contribute/myproposed', label: 'My proposed content' },
  { href: '/contribute/myapprovals', label: 'My approvals' },
];

const AREAS = ['Influence and Effectiveness', 'Sales Excellence', 'Management and Leadership'];
const statusBadge = { pending: 'pending', approved: 'completed', declined: 'cancelled' };

function Loading() {
  return <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}><Spinner /></div>;
}

const textareaStyle = {
  padding: '10px 13px', borderRadius: 'var(--radius-md)', border: '1px solid var(--line)',
  background: 'var(--canvas-white)', color: 'var(--ink)', fontSize: '0.9rem', outline: 'none',
  width: '100%', fontFamily: 'var(--font-body)', resize: 'vertical', minHeight: 80, boxSizing: 'border-box',
};

function FileLink({ p }) {
  if (!p.hasFile) return null;
  return (
    <button
      onClick={() => api.downloadProposalFile(p.id, p.fileName).catch(e => alert(e.message))}
      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--ink)', textDecoration: 'underline', fontSize: '0.82rem', fontFamily: 'var(--font-body)' }}
    >
      ↓ {p.fileName}
    </button>
  );
}

// ── Propose a Content ─────────────────────────────────────────────────────
function ProposeContent() {
  const [form, setForm] = useState({ title: '', description: '', area: AREAS[0] });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    if (!form.title.trim()) return;
    setBusy(true);
    try {
      await api.proposeContent({ ...form, file });
      setForm({ title: '', description: '', area: AREAS[0] });
      setFile(null);
      setSent(true);
    } catch (err) { alert(err.message); } finally { setBusy(false); }
  }

  return (
    <>
      <Alert type="info">
        Propose new content — it goes to the Custodians of the area you choose. Once approved it becomes
        HB property and appears in <b>Grow → Content by Open Innovation</b>.
      </Alert>
      <Card style={{ padding: '20px 22px', marginTop: 18, maxWidth: 620 }}>
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={labelStyle}>Headline</label>
            <Input placeholder="Headline" value={form.title} onChange={e => set('title', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Short description</label>
            <textarea placeholder="What is it, and what does it do?" value={form.description} onChange={e => set('description', e.target.value)} style={textareaStyle} />
          </div>
          <div>
            <label style={labelStyle}>Area</label>
            <Select value={form.area} onChange={e => set('area', e.target.value)}>
              {AREAS.map(a => <option key={a} value={a}>{a}</option>)}
            </Select>
          </div>
          <div>
            <label style={labelStyle}>File describing the content</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <label style={fileBtnStyle}>
                <input type="file" onChange={e => setFile(e.target.files[0] || null)} style={{ display: 'none' }} />
                <span style={{ fontSize: '0.95rem', lineHeight: 1 }}>↑</span> Choose file
              </label>
              <span style={{ fontSize: '0.82rem', color: file ? 'var(--ink)' : 'var(--ink-faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260 }}>
                {file ? file.name : 'No file selected'}
              </span>
              {file && (
                <button type="button" onClick={() => setFile(null)} title="Remove file"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-faint)', fontSize: '1rem', lineHeight: 1 }}>×</button>
              )}
            </div>
          </div>
          <Btn type="submit" loading={busy} style={{ alignSelf: 'flex-start' }}>Send to Custodians</Btn>
        </form>
        {sent && <div style={{ marginTop: 14 }}><Alert type="success">Your content has been sent to the Custodians for approval.</Alert></div>}
      </Card>
    </>
  );
}

// ── My proposed content ───────────────────────────────────────────────────
function MyProposed() {
  const [state, setState] = useState({ loading: true, error: null, proposals: [] });
  useEffect(() => {
    api.getMyProposals()
      .then(d => setState({ loading: false, error: null, proposals: d.proposals }))
      .catch(e => setState({ loading: false, error: e.message, proposals: [] }));
  }, []);

  if (state.loading) return <Loading />;
  if (state.error) return <Alert type="error">{state.error}</Alert>;
  if (!state.proposals.length) return <EmptyState icon="🌱" title="Nothing yet" message="Content you propose will appear here with its status." />;

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {state.proposals.map(p => (
        <Card key={p.id} style={{ padding: '16px 20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{p.title}</div>
            <Badge status={statusBadge[p.status] || 'default'}>{p.status}</Badge>
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--ink-faint)', marginTop: 2 }}>{p.area}{p.approver ? ` · decided by ${p.approver}` : ''}</div>
          {p.description && <div style={{ fontSize: '0.85rem', color: 'var(--ink-soft)', marginTop: 8, lineHeight: 1.5 }}>{p.description}</div>}
          {p.comment && <div style={{ fontSize: '0.8rem', color: 'var(--ink-soft)', marginTop: 6 }}>Note: {p.comment}</div>}
          <div style={{ marginTop: 8 }}><FileLink p={p} /></div>
        </Card>
      ))}
    </div>
  );
}

// ── My approvals (custodian) ──────────────────────────────────────────────
function ProposalDecideCard({ p, onDecide }) {
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  async function decide(decision) {
    setBusy(true);
    try { await onDecide(p.id, decision, comment); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  }
  return (
    <Card style={{ padding: '16px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{p.title}</div>
        <Badge status="default">{p.area}</Badge>
      </div>
      <div style={{ fontSize: '0.75rem', color: 'var(--ink-faint)', marginTop: 2 }}>{p.submittedBy ? `by ${p.submittedBy}` : ''}</div>
      {p.description && <div style={{ fontSize: '0.85rem', color: 'var(--ink-soft)', marginTop: 8, lineHeight: 1.5 }}>{p.description}</div>}
      <div style={{ marginTop: 8 }}><FileLink p={p} /></div>
      <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={comment} onChange={e => setComment(e.target.value)} placeholder="Comment (optional)"
          style={{ flex: 1, minWidth: 160, padding: '7px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--line)', fontSize: '0.82rem', fontFamily: 'var(--font-body)', outline: 'none' }} />
        <Btn size="sm" loading={busy} onClick={() => decide('approve')}>Approve — make HB property</Btn>
        <Btn size="sm" variant="outline" loading={busy} onClick={() => decide('decline')}>Decline</Btn>
      </div>
    </Card>
  );
}

function MyApprovals() {
  const [state, setState] = useState({ loading: true, error: null, proposals: [], areas: [] });
  const load = useCallback(() => {
    setState(s => ({ ...s, loading: true }));
    api.getProposalQueue()
      .then(d => setState({ loading: false, error: null, proposals: d.proposals, areas: d.areas }))
      .catch(e => setState({ loading: false, error: e.message, proposals: [], areas: [] }));
  }, []);
  useEffect(() => { load(); }, [load]);

  async function decide(id, decision, comment) {
    await api.decideProposal(id, decision, comment);
    load();
  }

  if (state.loading) return <Loading />;
  if (state.error) return <Alert type="error">{state.error}</Alert>;
  if (!state.areas.length) return <EmptyState icon="🛡" title="Not a Custodian" message="Content approvals show here for Custodians of an area." />;

  return (
    <>
      <Alert type="info">Proposals awaiting your decision as Custodian of: <b>{state.areas.join(', ')}</b>. Approve and the content becomes HB property.</Alert>
      <div style={{ marginTop: 16 }}>
        {state.proposals.length === 0
          ? <EmptyState icon="✓" title="Nothing to decide" message="No pending proposals in your areas." />
          : <div style={{ display: 'grid', gap: 12 }}>{state.proposals.map(p => <ProposalDecideCard key={p.id} p={p} onDecide={decide} />)}</div>}
      </div>
    </>
  );
}

const TITLES = {
  propose: 'Propose a Content',
  myproposed: 'My proposed content',
  myapprovals: 'My approvals',
};

export default function Contribute() {
  const { sub } = useParams();
  if (!sub) return <Navigate to="/contribute/propose" replace />;

  let body;
  if (sub === 'propose') body = <ProposeContent />;
  else if (sub === 'myproposed') body = <MyProposed />;
  else if (sub === 'myapprovals') body = <MyApprovals />;
  else return <Navigate to="/contribute/propose" replace />;

  return (
    <BasecampLayout>
      <PageHeader title="Contribute — Open Innovation, governed" subtitle={TITLES[sub]} />
      <AreaLayout items={SUBNAV}>{body}</AreaLayout>
    </BasecampLayout>
  );
}

const labelStyle = { display: 'block', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--ink-faint)', marginBottom: 5 };
const fileBtnStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
  padding: '8px 16px', borderRadius: 'var(--radius-md)',
  border: '1.5px solid var(--ink-faint)', background: 'transparent', color: 'var(--ink)',
  fontSize: '0.85rem', fontWeight: 600, fontFamily: 'var(--font-body)', whiteSpace: 'nowrap',
};
